import {
  Alert,
  Box,
  Container,
  HStack,
  Heading,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import {
  Link as RouterLink,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { FiCheck, FiClock, FiMail, FiRefreshCw, FiX } from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import useAuth from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"

type StandardResponse<T> = {
  status: "success" | "error"
  data?: T | null
  message?: string
}

const unwrapResponse = <T,>(response: StandardResponse<T>) => {
  if (response.status === "success") {
    return response.data ?? null
  }
  throw new Error(response.message ?? "Request failed")
}

export interface EmailVerificationNoticeProps {
  userEmail?: string
  showResendButton?: boolean
  autoRedirectOnVerification?: boolean
  redirectTo?: string
}

export const EmailVerificationNotice = ({
  userEmail,
  showResendButton = true,
  autoRedirectOnVerification = true,
  redirectTo = "/dashboard",
}: EmailVerificationNoticeProps) => {
  const [countdown, setCountdown] = useState(0)
  const { user } = useAuth()
  const router = useRouter()
  const searchParams = useSearch({ strict: false }) as Record<string, unknown>
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const email = userEmail || user?.email
  const token =
    typeof searchParams.token === "string"
      ? searchParams.token
      : typeof searchParams.verification_token === "string"
        ? searchParams.verification_token
        : undefined

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const verifyEmailMutation = useMutation({
    mutationFn: async (verificationToken: string) => {
      const response = await api.auth.verifyEmailApiV1AuthVerifyEmailPost({
        requestBody: { token: verificationToken },
      })
      return unwrapResponse(response)
    },
    onSuccess: () => {
      showSuccessToast("Email verified successfully!")
      if (autoRedirectOnVerification) {
        router.navigate({ to: redirectTo })
      }
    },
    onError: (error: Error) => {
      showErrorToast(
        `Email verification failed: ${error?.message || "Invalid or expired token"}`,
      )
    },
  })

  const resendMutation = useMutation({
    mutationFn: async () => {
      if (!email) {
        throw new Error("Email address required to resend verification")
      }
      const response =
        await api.auth.resendVerificationEmailApiV1AuthResendVerificationPost({
          requestBody: { email },
        })
      return unwrapResponse(response)
    },
    onSuccess: () => {
      showSuccessToast("Verification email sent successfully")
      setCountdown(60) // 60 second cooldown
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to resend verification email: ${error?.message || "Unknown error"}`,
      )
    },
  })

  // Auto-verify if token is present in URL
  useEffect(() => {
    if (token && !verifyEmailMutation.isPending) {
      verifyEmailMutation.mutate(token)
    }
  }, [token])

  // If verification is in progress
  if (verifyEmailMutation.isPending) {
    return (
      <Container maxW="md" py={8}>
        <Stack gap={6} align="center">
          <Box p={3} borderRadius="full" bg="blue.100" color="blue.600">
            <FiRefreshCw size={24} className="animate-spin" />
          </Box>
          <Heading size="lg" textAlign="center">
            Verifying Email...
          </Heading>
          <Text color="gray.600" textAlign="center">
            Please wait while we verify your email address.
          </Text>
        </Stack>
      </Container>
    )
  }

  // If verification was successful
  if (verifyEmailMutation.isSuccess) {
    return (
      <Container maxW="md" py={8}>
        <Stack gap={6} align="center">
          <Box p={3} borderRadius="full" bg="green.100" color="green.600">
            <FiCheck size={24} />
          </Box>
          <Heading size="lg" textAlign="center" color="green.600">
            Email Verified!
          </Heading>
          <Text color="gray.600" textAlign="center">
            Your email address has been successfully verified. You can now
            access all features of your account.
          </Text>
          <RouterLink to={redirectTo}>
            <Button colorScheme="green" size="lg" w="full">
              Continue to Dashboard
            </Button>
          </RouterLink>
        </Stack>
      </Container>
    )
  }

  // If verification failed
  if (verifyEmailMutation.isError) {
    return (
      <Container maxW="md" py={8}>
        <Stack gap={6}>
          <Stack align="center" gap={3}>
            <Box p={3} borderRadius="full" bg="red.100" color="red.600">
              <FiX size={24} />
            </Box>
            <Heading size="lg" textAlign="center" color="red.600">
              Verification Failed
            </Heading>
            <Text color="gray.600" textAlign="center">
              The verification link is invalid or has expired.
            </Text>
          </Stack>

          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Title>Verification Error</Alert.Title>
            <Alert.Description>
              {verifyEmailMutation.error?.message ||
                "The verification token is invalid or has expired."}
            </Alert.Description>
          </Alert.Root>

          {email && showResendButton && (
            <Stack gap={3}>
              <Button
                onClick={() => resendMutation.mutate()}
                loading={resendMutation.isPending}
                disabled={countdown > 0}
                colorScheme="blue"
                w="full"
                gap={2}
              >
                <FiMail />
                {countdown > 0
                  ? `Resend in ${countdown}s`
                  : "Send New Verification Email"}
              </Button>

              <RouterLink to="/login">
                <Button variant="outline" w="full">
                  Back to Login
                </Button>
              </RouterLink>
            </Stack>
          )}
        </Stack>
      </Container>
    )
  }

  // Default verification notice
  return (
    <Container maxW="md" py={8}>
      <Stack gap={6}>
        {/* Header */}
        <Stack align="center" gap={3}>
          <Box p={3} borderRadius="full" bg="blue.100" color="blue.600">
            <FiMail size={24} />
          </Box>
          <Heading size="lg" textAlign="center">
            Verify Your Email
          </Heading>
          <Text color="gray.600" textAlign="center">
            We've sent a verification email to
          </Text>
          {email && (
            <Text fontWeight="semibold" color="blue.600">
              {email}
            </Text>
          )}
        </Stack>

        {/* Instructions */}
        <Stack gap={4} p={6} bg="gray.50" borderRadius="lg">
          <Text fontSize="sm" color="gray.700" textAlign="center">
            Click the verification link in the email to activate your account.
            If you don't see the email, check your spam folder.
          </Text>

          <Alert.Root status="info" size="sm">
            <Alert.Indicator />
            <Alert.Description>
              Verification links expire after 24 hours for security reasons.
            </Alert.Description>
          </Alert.Root>
        </Stack>

        {/* Action Buttons */}
        {email && showResendButton && (
          <Stack gap={3}>
            <Button
              onClick={() => resendMutation.mutate()}
              loading={resendMutation.isPending}
              disabled={countdown > 0}
              colorScheme="blue"
              w="full"
            >
              <HStack>
                {countdown > 0 ? <FiClock /> : <FiMail />}
                <Text>
                  {countdown > 0
                    ? `Resend in ${countdown}s`
                    : "Resend Verification Email"}
                </Text>
              </HStack>
            </Button>

            <RouterLink to="/login">
              <Button variant="outline" w="full">
                Back to Login
              </Button>
            </RouterLink>
          </Stack>
        )}

        {/* Help Text */}
        <Text textAlign="center" fontSize="sm" color="gray.500">
          Having trouble?{" "}
          <Link color="blue.500" href="mailto:support@example.com">
            Contact Support
          </Link>
        </Text>
      </Stack>
    </Container>
  )
}

export default EmailVerificationNotice
